import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Settings, Save, AlertCircle, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

// IQC Output Grades (for repaired devices)
const GRADES = ['Excellent', 'Superb', 'Good', 'Fair', 'Okay'];

const QCMasterConfig = ({ user, onLogout }) => {
  const [template, setTemplate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedParam, setExpandedParam] = useState(null);
  const [expandedOption, setExpandedOption] = useState(null);
  const [newPartInputs, setNewPartInputs] = useState({});

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/iqc/templates/Android`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Initialize grade-wise parts if not exists
      const updatedCategories = response.data.categories.map(cat => ({
        ...cat,
        parameters: cat.parameters.map(param => ({
          ...param,
          options: param.options.map(opt => ({
            ...opt,
            grade_parts_mapping: opt.grade_parts_mapping || {}
          }))
        }))
      }));
      
      setTemplate(response.data);
      setCategories(updatedCategories);
      if (updatedCategories.length > 0) {
        setExpandedCategory(updatedCategories[0].category_id);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      alert('Failed to load configuration. Please check backend connection.');
    } finally {
      setLoading(false);
    }
  };

  const updateSeverity = (categoryId, paramId, optionIndex, newSeverity) => {
    const updatedCategories = categories.map(cat => {
      if (cat.category_id === categoryId) {
        const updatedParams = cat.parameters.map(param => {
          if (param.parameter_id === paramId) {
            const updatedOptions = param.options.map((opt, idx) => {
              if (idx === optionIndex) {
                return { ...opt, severity: newSeverity };
              }
              return opt;
            });
            return { ...param, options: updatedOptions };
          }
          return param;
        });
        return { ...cat, parameters: updatedParams };
      }
      return cat;
    });
    setCategories(updatedCategories);
    setHasChanges(true);
  };

  const updateEngineerLevel = (categoryId, paramId, optionIndex, level) => {
    const updatedCategories = categories.map(cat => {
      if (cat.category_id === categoryId) {
        const updatedParams = cat.parameters.map(param => {
          if (param.parameter_id === paramId) {
            const updatedOptions = param.options.map((opt, idx) => {
              if (idx === optionIndex) {
                return { ...opt, triggers_engineer_level: level };
              }
              return opt;
            });
            return { ...param, options: updatedOptions };
          }
          return param;
        });
        return { ...cat, parameters: updatedParams };
      }
      return cat;
    });
    setCategories(updatedCategories);
    setHasChanges(true);
  };

  const addPartForGrade = (categoryId, paramId, optionIndex, grade) => {
    const key = `${categoryId}-${paramId}-${optionIndex}-${grade}`;
    const partName = newPartInputs[key];
    
    if (!partName || partName.trim() === '') return;

    const updatedCategories = categories.map(cat => {
      if (cat.category_id === categoryId) {
        const updatedParams = cat.parameters.map(param => {
          if (param.parameter_id === paramId) {
            const updatedOptions = param.options.map((opt, idx) => {
              if (idx === optionIndex) {
                const gradeMapping = opt.grade_parts_mapping || {};
                const currentParts = gradeMapping[grade] || [];
                if (!currentParts.includes(partName.trim())) {
                  return {
                    ...opt,
                    grade_parts_mapping: {
                      ...gradeMapping,
                      [grade]: [...currentParts, partName.trim()]
                    }
                  };
                }
              }
              return opt;
            });
            return { ...param, options: updatedOptions };
          }
          return param;
        });
        return { ...cat, parameters: updatedParams };
      }
      return cat;
    });
    
    setCategories(updatedCategories);
    setNewPartInputs({ ...newPartInputs, [key]: '' });
    setHasChanges(true);
  };

  const removePartForGrade = (categoryId, paramId, optionIndex, grade, partName) => {
    const updatedCategories = categories.map(cat => {
      if (cat.category_id === categoryId) {
        const updatedParams = cat.parameters.map(param => {
          if (param.parameter_id === paramId) {
            const updatedOptions = param.options.map((opt, idx) => {
              if (idx === optionIndex) {
                const gradeMapping = opt.grade_parts_mapping || {};
                const updatedParts = (gradeMapping[grade] || []).filter(p => p !== partName);
                return {
                  ...opt,
                  grade_parts_mapping: {
                    ...gradeMapping,
                    [grade]: updatedParts
                  }
                };
              }
              return opt;
            });
            return { ...param, options: updatedOptions };
          }
          return param;
        });
        return { ...cat, parameters: updatedParams };
      }
      return cat;
    });
    setCategories(updatedCategories);
    setHasChanges(true);
  };

  const saveConfiguration = async () => {
    if (!window.confirm('Save all changes to severity levels, engineer levels, and grade-wise parts mapping?')) return;

    try {
      const token = localStorage.getItem('token');
      const updatedTemplate = {
        ...template,
        categories: categories
      };
      
      await axios.put(
        `${API_URL}/api/iqc/templates/${template.template_id}`,
        updatedTemplate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Configuration saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'NTF': 'border-green-500 bg-green-50',
      'Low': 'border-yellow-500 bg-yellow-50',
      'Medium': 'border-orange-500 bg-orange-50',
      'High': 'border-red-500 bg-red-50',
      'Critical_L1': 'border-purple-500 bg-purple-50',
      'Critical_L4': 'border-purple-600 bg-purple-50'
    };
    return colors[severity] || 'border-gray-300 bg-gray-50';
  };

  const getGradeColor = (grade) => {
    const colors = {
      'Excellent': 'bg-green-600',
      'Superb': 'bg-green-500',
      'Good': 'bg-blue-500',
      'Fair': 'bg-yellow-500',
      'Okay': 'bg-orange-500'
    };
    return colors[grade] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Master Configuration...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center" style={{ color: ZOBOX_BLUE }}>
              <Settings className="w-8 h-8 mr-3" />
              IQC Master Configuration
            </h1>
            <p className="text-gray-600 mt-1">
              Configure IQC: Severity, Engineer Level & Output Grade-Wise Parts Mapping
            </p>
          </div>
          <button
            onClick={saveConfiguration}
            disabled={!hasChanges}
            className={`flex items-center px-6 py-3 rounded-lg font-semibold ${
              hasChanges
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save className="w-5 h-5 mr-2" />
            Save All Changes
            {hasChanges && <span className="ml-2 px-2 py-1 bg-red-500 text-xs rounded-full">*</span>}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">IQC Output Grade-Wise Parts Mapping:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Purpose:</strong> Diagnose devices from external suppliers & plan repairs</li>
                <li><strong>Output Grades:</strong> Excellent, Superb, Good, Fair, Okay</li>
                <li><strong>Devices:</strong> Android & iOS (iOS-specific parameters included)</li>
                <li>Parts are <strong>recommendations</strong> based on target output grade</li>
                <li>Configure severity level and repair level (L0-L4) for each parameter option</li>
                <li>Different parts may be needed to achieve different output grades</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-4">
          {categories.map((category) => (
            <div key={category.category_id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => setExpandedCategory(
                  expandedCategory === category.category_id ? null : category.category_id
                )}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div className="text-left">
                    <h3 className="text-lg font-bold" style={{ color: ZOBOX_BLUE }}>
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {category.parameters?.length || 0} parameters
                    </p>
                  </div>
                </div>
                {expandedCategory === category.category_id ? <ChevronUp /> : <ChevronDown />}
              </button>

              {/* Category Content */}
              {expandedCategory === category.category_id && (
                <div className="p-6 bg-gray-50 space-y-4">
                  {category.parameters.map((param) => (
                    <div key={param.parameter_id} className="bg-white rounded-lg border-2 border-gray-200">
                      {/* Parameter Header */}
                      <button
                        onClick={() => {
                          console.log('Parameter clicked:', param.parameter_id);
                          setExpandedParam(
                            expandedParam === param.parameter_id ? null : param.parameter_id
                          );
                        }}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="text-left">
                          <h4 className="text-lg font-bold text-gray-800">
                            {param.order}. {param.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {param.options?.length || 0} options | Type: {param.input_type}
                          </p>
                        </div>
                        {expandedParam === param.parameter_id ? <ChevronUp /> : <ChevronDown />}
                      </button>

                      {/* Parameter Options */}
                      {expandedParam === param.parameter_id && (
                        <div className="p-4 space-y-4 bg-gray-50">
                          {param.options.map((option, optIdx) => {
                            const optionKey = `${category.category_id}-${param.parameter_id}-${optIdx}`;
                            return (
                              <div
                                key={optIdx}
                                className={`p-4 rounded-lg border-2 ${getSeverityColor(option.severity)}`}
                              >
                                {/* Option Header */}
                                <button
                                  onClick={() => setExpandedOption(
                                    expandedOption === optionKey ? null : optionKey
                                  )}
                                  className="w-full flex items-center justify-between"
                                >
                                  <div className="text-left flex-1">
                                    <div className="font-bold text-gray-800 mb-2">{option.value}</div>
                                    <div className="flex gap-4 text-sm">
                                      <span>Severity: <strong>{option.severity}</strong></span>
                                      <span>Engineer: <strong>{option.triggers_engineer_level || 'L0'}</strong></span>
                                    </div>
                                  </div>
                                  {expandedOption === optionKey ? <ChevronUp /> : <ChevronDown />}
                                </button>

                                {/* Option Details */}
                                {expandedOption === optionKey && (
                                  <div className="mt-4 pt-4 border-t-2 border-gray-300 space-y-4">
                                    {/* Severity & Engineer Level */}
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">
                                          SEVERITY LEVEL
                                        </label>
                                        <div className="space-y-1">
                                          {['NTF', 'Low', 'Medium', 'High', 'Critical_L1', 'Critical_L4'].map((sev) => (
                                            <label key={sev} className="flex items-center gap-2 cursor-pointer">
                                              <input
                                                type="radio"
                                                name={`severity-${optionKey}`}
                                                checked={option.severity === sev}
                                                onChange={() => updateSeverity(category.category_id, param.parameter_id, optIdx, sev)}
                                                className="w-4 h-4"
                                              />
                                              <span className="text-sm">{sev}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>

                                      <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-2">
                                          REPAIR LEVEL
                                        </label>
                                        <select
                                          value={option.triggers_engineer_level || 'L0'}
                                          onChange={(e) => updateEngineerLevel(category.category_id, param.parameter_id, optIdx, e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                        >
                                          <option value="L0">L0 - No Issue</option>
                                          <option value="L1">L1 - Basic / Software</option>
                                          <option value="L2">L2 - Intermediate</option>
                                          <option value="L3">L3 - Advanced</option>
                                          <option value="L4">L4 - Expert / Board Level</option>
                                        </select>
                                      </div>
                                    </div>

                                    {/* Grade-Wise Parts Mapping */}
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-3">
                                        IQC OUTPUT GRADE-WISE SPARE PARTS MAPPING (Recommended)
                                      </label>
                                      <div className="grid grid-cols-1 gap-3">
                                        {GRADES.map((grade) => {
                                          const gradeKey = `${optionKey}-${grade}`;
                                          const gradeParts = (option.grade_parts_mapping || {})[grade] || [];
                                          return (
                                            <div key={grade} className="border border-gray-300 rounded-lg p-3">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-3 py-1 ${getGradeColor(grade)} text-white text-xs font-bold rounded`}>
                                                  {grade}
                                                </span>
                                                <span className="text-xs text-gray-600">Parts to achieve this grade:</span>
                                              </div>
                                              
                                              <div className="flex flex-wrap gap-1 mb-2">
                                                {gradeParts.map((part, partIdx) => (
                                                  <span
                                                    key={partIdx}
                                                    className="px-2 py-1 bg-white border border-gray-400 rounded text-xs flex items-center gap-1"
                                                  >
                                                    {part}
                                                    <button
                                                      onClick={() => removePartForGrade(category.category_id, param.parameter_id, optIdx, grade, part)}
                                                      className="text-red-600 hover:text-red-800"
                                                    >
                                                      <X className="w-3 h-3" />
                                                    </button>
                                                  </span>
                                                ))}
                                              </div>

                                              <div className="flex gap-1">
                                                <input
                                                  type="text"
                                                  value={newPartInputs[gradeKey] || ''}
                                                  onChange={(e) => setNewPartInputs({ ...newPartInputs, [gradeKey]: e.target.value })}
                                                  onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      addPartForGrade(category.category_id, param.parameter_id, optIdx, grade);
                                                    }
                                                  }}
                                                  placeholder="Add part for this grade"
                                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                                                />
                                                <button
                                                  onClick={() => addPartForGrade(category.category_id, param.parameter_id, optIdx, grade)}
                                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                >
                                                  <Plus className="w-3 h-3" />
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Floating Save Button */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6 z-50">
            <button
              onClick={saveConfiguration}
              className="flex items-center px-6 py-4 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 font-bold text-lg"
            >
              <Save className="w-6 h-6 mr-2" />
              Save Configuration
              <span className="ml-2 px-2 py-1 bg-red-500 text-xs rounded-full">Unsaved</span>
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QCMasterConfig;
